import { Immutable } from "immer";
import React from "react";

import type { PreApprovalBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { FinancingModalTriggerBlock } from "../../apps/financing/FinancingModalTriggerBlock";
import {
    FinancingCardType,
    FinancingModalTriggerTheme,
    isFinancingModalTriggerTheme,
} from "../../constants";
import { usePageSetting } from "../../utils/settings";
import { usePageURL } from "../../utils/urls";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Maps a site's ``app_slug`` to its branded financing card. Adapted from the
 * private ``getCardTypeByAppSlug`` in
 * ``apps/financing/FinancingModalTrigger/Configurator.tsx`` (that copy is not
 * exported and we may only add new files here).
 *
 * The financing card is a BUILD-level concept, not a per-brand one: the legacy
 * per-site ``FinancingModalTriggerBlockTemplate`` hardcoded a single card type
 * for an entire build, with no brand conditional. So every ``app_slug`` a build
 * serves must map to that build's card here, or PreApprovalBlock renders an
 * empty card (the legacy ``<span />`` stub) for the unmapped sub-brands.
 *
 * The tempurpedic build serves not only ``tempurpedic`` but its sub-brands
 * ``sale2``, ``tempurca``, ``tempurwarranty`` and the (surprisingly named, but
 * tempurpedic-built) ``sealywarranty`` / ``stearnswarranty`` warranty sites --
 * all of which the legacy template rendered with the Tempur-Pedic card. The
 * Configurator copy omits these sub-brands because its context only ever runs
 * under a main-brand ``app_slug``; do NOT mirror this list back to it.
 *
 * Sites with no financing card (pillows / toppers / one800pillows) return
 * ``null`` and render an empty inner element, reproducing their legacy
 * ``<span />`` stub. Candidate for hoisting to a shared util.
 */
const getCardTypeByAppSlug = (
    appSlug: string | null,
): FinancingCardType | null => {
    switch (appSlug) {
        case "sale2":
        case "sealywarranty":
        case "stearnswarranty":
        case "tempurca":
        case "tempurpedic":
        case "tempurwarranty": {
            return FinancingCardType.TEMPUR_CC;
        }
        case "stearns": {
            return FinancingCardType.STEARNS_CC;
        }
        case "sealy": {
            return FinancingCardType.SEALY_CC;
        }
        case "cocoon": {
            return FinancingCardType.COCOON_CC;
        }
    }
    return null;
};

/**
 * Faithful port of the legacy server-rendered
 * ``tsicommon_cms/blocks/pre-approval-block.html``. The per-site
 * ``PreQualificationSettings.toolbox`` flag decides whether this banner renders
 * at all. That gate is now serialized onto the block value as
 * ``value.toolbox_enabled`` (see ``PreApprovalBlock.get_data_for_client_render``)
 * rather than living in the Django template's ``{% if %}`` — so BOTH the
 * consolidated React page-body assembly (which dispatches straight to this
 * component, bypassing the ``.html``) and the standalone ``{% include_react %}``
 * path honor the toggle. When the flag is off we render nothing.
 *
 * Legacy markup nested a second ``{% include_react FinancingModalTriggerBlockTemplate
 * theme=value.theme application_source="Financing Page" %}`` inside the
 * ``<section class="pre-approval-container ...">``. Each site resolved that
 * inner template to its own ``FinancingModalTriggerBlockTemplate`` (supplying a
 * branded ``cardType`` / ``aprInfoID`` / ``financeLink``). Following the
 * established port pattern (see ``EverydayOfferGridBlock`` and
 * ``FinancingModalTriggerConfigurator``), we render that child by direct React
 * composition instead, deriving the site-specific ``cardType`` from the global
 * ``app_slug`` page setting — so all seven sites can share this one component.
 *
 * ``application_source`` is the hardcoded analytics id from the legacy markup
 * (an internal GA value, not user-facing) and is intentionally not translated.
 */
export const PreApprovalBlock = (props: IProps): React.JSX.Element | null => {
    const v = props.value;
    const appSlug = usePageSetting("app_slug");
    const cardType = getCardTypeByAppSlug(appSlug);
    // Extract into a `string` local first so the type guard narrows it to the
    // enum without needing a cast (mirrors the per-site templates).
    const rawTheme: string = v.theme ?? "";
    const theme = isFinancingModalTriggerTheme(rawTheme)
        ? rawTheme
        : FinancingModalTriggerTheme.DEFAULT;
    const financeLink = usePageURL("finance_link");
    const homeLink = usePageURL("home");
    // ``PreQualificationSettings.toolbox`` global-settings gate (server-side
    // state, serialized onto the block value). When off, render nothing —
    // matching the legacy ``{% if prequal_settings.toolbox %}``. Gated AFTER all
    // hook calls to comply with the React Rules of Hooks.
    if (!v.toolbox_enabled) {
        return null;
    }
    return (
        <section
            className={concatClassNames([
                "pre-approval-container",
                v.theme ?? "",
                v.background_style?.style ?? "",
                v.responsive_visibility?.visibility ?? "",
            ])}
        >
            {cardType ? (
                // The wrapping <div> mirrors the per-site
                // FinancingModalTriggerBlockTemplate's own wrapper element.
                <div>
                    <FinancingModalTriggerBlock
                        cardType={cardType}
                        theme={theme}
                        aprInfoID={"#apr-information"}
                        applicationSource={"Financing Page"}
                        financeLink={financeLink || homeLink}
                    />
                </div>
            ) : (
                // Sites without a branded financing card rendered the legacy
                // FinancingModalTriggerBlockTemplate <span /> stub.
                <span />
            )}
        </section>
    );
};
